import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock, Lock, Plus, Users, X } from 'lucide-react';
import type { Employee, EmployeeList, EmployeeStatus, PeopleSummary } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useParameterOptions } from '../../lib/parameters';
import { pick } from '../../i18n';
import { Badge, ClickableCard, EmptyState, PageHeader, SearchableSelect, TableSkeleton, cn } from '../../components/ui';
import { EmployeeForm } from './forms';
import { parsePeopleSearch, type PeopleSearch } from './search';
import { Avatar, EMPLOYEE_STATUSES, EMPLOYEE_TONE, UtilisationBar, useEntityOptions } from './shared';

const PAGE_SIZE = 24;
const SORTS = ['name_en', 'employee_no', '-hire_date'];

/** Staff directory (central): who works here, for whom they work today and how much of their time is taken. */
export function PeoplePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const search = parsePeopleSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const [creating, setCreating] = useState(false);
  const departments = useParameterOptions('department');
  const entities = useEntityOptions();
  const seesAllocations = can('allocations');
  const sort = search.sort ?? 'name_en';
  const query = { q: search.q, status: search.status, department_code: search.department, manager_id: search.manager, entity_id: search.entity, over_allocated: search.over ? 'true' : undefined, unallocated: search.free ? 'true' : undefined, sort, page: search.page ?? 1, page_size: PAGE_SIZE };
  const list = useQuery({ queryKey: ['people', 'employees', query], queryFn: () => api.get<EmployeeList>(`/api/people/employees${qs(query)}`), placeholderData: (prev) => prev });
  const summary = useQuery({ queryKey: ['people', 'summary'], queryFn: () => api.get<PeopleSummary>('/api/people/summary'), enabled: seesAllocations, staleTime: 30_000 });
  const setSearch = (patch: Partial<PeopleSearch>, resetPage = true) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    if (resetPage) delete next.page;
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
    void navigate({ to: '/people', search: next as PeopleSearch });
  };
  const filtered = Boolean(search.q || search.status || search.department || search.manager || search.entity || search.over || search.free);
  const page = search.page ?? 1;
  const pages = list.data ? Math.max(1, Math.ceil(list.data.meta.total / PAGE_SIZE)) : 1;
  const s = summary.data;

  return (
    <div>
      <PageHeader
        title={t('people.pageTitle')}
        subtitle={list.data ? t('people.count', { count: list.data.meta.total }) : undefined}
        actions={
          <>
            {seesAllocations && <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/people/allocations', search: {} })}><CalendarClock size={16} /> {t('people.allocationsPage')}</button>}
            {can('people', 'can_create') && <button type="button" className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> {t('people.newEmployee')}</button>}
          </>
        }
      />

      {s && (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label={t('people.headcount')} value={String(s.headcount)} active={!filtered} onClick={() => void navigate({ to: '/people', search: {} })} />
          <Tile label={t('people.averageUtilisation')} value={`${s.average_utilisation}%`} />
          <Tile label={t('people.unallocated')} value={String(s.unallocated)} active={Boolean(search.free)} onClick={() => setSearch({ free: search.free ? undefined : '1', over: undefined })} />
          <Tile label={t('people.overAllocated')} value={String(s.over_allocated.length)} tone={s.over_allocated.length > 0 ? 'danger' : undefined} active={Boolean(search.over)} onClick={() => setSearch({ over: search.over ? undefined : '1', free: undefined })} />
        </div>
      )}

      <div className="mb-3 grid gap-2 md:grid-cols-5">
        <input className="input md:col-span-2" placeholder={t('people.searchPlaceholder')} defaultValue={search.q ?? ''} key={search.q ?? ''} onKeyDown={(e) => { if (e.key === 'Enter') setSearch({ q: (e.target as HTMLInputElement).value || undefined }); }} onBlur={(e) => { if ((e.target.value || undefined) !== search.q) setSearch({ q: e.target.value || undefined }); }} />
        <SearchableSelect value={search.department ?? ''} onChange={(v) => setSearch({ department: (v as string) || undefined })} options={[{ value: '', label: t('people.anyDepartment') }, ...departments.options]} placeholder={t('people.anyDepartment')} />
        <SearchableSelect value={search.entity ?? ''} onChange={(v) => setSearch({ entity: (v as string) || undefined })} options={[{ value: '', label: t('people.anyEntity') }, ...entities.options]} placeholder={t('people.anyEntity')} />
        <SearchableSelect value={search.status ?? ''} onChange={(v) => setSearch({ status: (v as EmployeeStatus) || undefined })} options={[{ value: '', label: t('people.anyStatus') }, ...EMPLOYEE_STATUSES.map((x) => ({ value: x, label: t(`people.statuses.${x}`) }))]} placeholder={t('people.anyStatus')} />
        <div className="flex flex-wrap items-center gap-3 text-sm md:col-span-5">
          <div className="w-52"><SearchableSelect value={sort} onChange={(v) => setSearch({ sort: v as string })} options={SORTS.map((x) => ({ value: x, label: t(`people.sorts.${x.replace('-', 'desc_')}`) }))} /></div>
          {filtered && <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => void navigate({ to: '/people', search: {} })}><X size={12} /> {t('people.clearFilters')}</button>}
        </div>
      </div>

      {list.isLoading && <TableSkeleton />}
      {list.data && list.data.items.length === 0 && <EmptyState>{filtered ? t('people.noMatch') : t('people.none')}</EmptyState>}
      {list.data && list.data.items.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {list.data.items.map((e) => <EmployeeCard key={e.id} e={e} showUtilisation={seesAllocations} onOpen={() => void navigate({ to: '/people/$id', params: { id: e.id } })} />)}
        </div>
      )}
      {list.data && list.data.meta.total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs">
          <button type="button" className="btn-secondary px-2 py-1" disabled={page <= 1} onClick={() => setSearch({ page: page - 1 > 1 ? page - 1 : undefined }, false)}>{t('app.previous')}</button>
          <span>{page} / {pages}</span>
          <button type="button" className="btn-secondary px-2 py-1" disabled={page >= pages} onClick={() => setSearch({ page: page + 1 }, false)}>{t('app.next')}</button>
        </div>
      )}
      <EmployeeForm open={creating} onClose={() => setCreating(false)} onSaved={(e) => void navigate({ to: '/people/$id', params: { id: e.id } })} />
    </div>
  );
}

/** A headline figure; when it filters the list it is a button and shows that it is on. */
function Tile({ label, value, tone, active, onClick }: { label: string; value: string; tone?: 'danger'; active?: boolean; onClick?: () => void }) {
  const body = (
    <>
      <div className={cn('text-2xl font-semibold tabular-nums', tone === 'danger' && 'text-red-600')}>{value}</div>
      <div className="text-xs text-neutral-600 dark:text-neutral-300">{label}</div>
    </>
  );
  if (!onClick) return <div className="card p-3">{body}</div>;
  return <button type="button" aria-pressed={active} onClick={onClick} className={cn('card p-3 text-start transition hover:border-brand-500', active && 'border-brand-600 bg-brand-50 dark:bg-brand-700/20')}>{body}</button>;
}

function EmployeeCard({ e, showUtilisation, onOpen }: { e: Employee; showUtilisation: boolean; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const name = pick(i18n.language, e.display_name_en, e.display_name_ar);
  const title = pick(i18n.language, e.job_title_en ?? '', e.job_title_ar ?? '');
  return (
    <ClickableCard onOpen={onOpen} label={name} footer={<><span dir="ltr">{e.employee_no}</span>{e.direct_reports > 0 && <span className="inline-flex items-center gap-1"><Users size={12} />{t('people.reports', { count: e.direct_reports })}</span>}{e.personal_masked && <span className="inline-flex items-center gap-1" title={t('people.personalHidden')}><Lock size={12} /></span>}</>}>
      <div className="flex items-start gap-3">
        <Avatar person={e} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1 font-semibold group-hover:text-brand-700 dark:group-hover:text-brand-100" dir="auto">{name}{e.status !== 'active' && <Badge tone={EMPLOYEE_TONE[e.status]}>{t(`people.statuses.${e.status}`)}</Badge>}</div>
          {title && <div className="truncate text-sm text-neutral-600 dark:text-neutral-400" dir="auto">{title}</div>}
          <div className="mt-1 flex flex-wrap gap-1">{e.department && <Badge>{pick(i18n.language, e.department.name_en, e.department.name_ar)}</Badge>}{e.employment_type && <Badge tone="info">{pick(i18n.language, e.employment_type.name_en, e.employment_type.name_ar)}</Badge>}</div>
        </div>
      </div>
      {e.manager && <div className="mt-2 truncate text-xs text-neutral-500" dir="auto">{t('people.reportsTo')}: {pick(i18n.language, e.manager.display_name_en, e.manager.display_name_ar)}</div>}
      {showUtilisation && e.status === 'active' && (
        <div className="mt-2">
          <div className="mb-0.5 flex items-center justify-between text-xs text-neutral-500"><span>{t('people.allocatedToday', { count: e.active_allocations })}</span>{e.over_allocated && <span className="inline-flex items-center gap-1 font-medium text-red-600"><AlertTriangle size={12} />{t('people.overAllocated')}</span>}</div>
          <UtilisationBar percent={e.allocation_percent} />
        </div>
      )}
    </ClickableCard>
  );
}
